import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateVehicleCategory, useUpdateVehicleCategory } from "../../../../../common/api/queries/vehicleCategory";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { VehicleCategory } from "../../../../contract/enums";
import { CertificateVehicleCategory, CreateUpdateCertificateVehicleCategory } from "../../types";

interface Props {
  category?: CertificateVehicleCategory;
  open: boolean;
  onFormCancel: () => void;
}

export const VehicleCategoryForm = ({ category, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const createVehicleCategory = useCreateVehicleCategory();
  const updateVehicleCategory = useUpdateVehicleCategory();

  const [form] = Form.useForm<CreateUpdateCertificateVehicleCategory>();

  useEffect(() => {
    if (open && category) {
      form.setFieldsValue(category);
    }
  }, [category, open, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (category) {
          updateVehicleCategory.mutate(
            { id: category.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "vehicleCategory.attrs", error.violations),
            },
          );
        } else {
          createVehicleCategory.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "vehicleCategory.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={category ? t("vehicleCategory.titles.updateCategory") : t("vehicleCategory.titles.createCategory")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createVehicleCategory.isPending || updateVehicleCategory.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={category} />

      <Form form={form} layout="vertical" name="vehicleCategoryForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item name="category" label={t("contract.enums.vehicleCategory._label")} rules={[validations.notNull]}>
              <Select
                {...selectStandardProps}
                options={Object.keys(VehicleCategory).map((category) => ({
                  value: category,
                  label: t("contract.enums.vehicleCategory." + category),
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="value"
              label={t("vehicleCategory.attrs.value")}
              rules={[
                validations.notNull,
                validations.size(1, 64),
                validations.pattern(regexPatterns.vehicleCategoryValueRegex),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t("vehicleCategory.attrs.description")}
              rules={[validations.notNull, validations.size(1, 255)]}
            >
              <Input.TextArea autoSize={{ minRows: 4, maxRows: 7 }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
